import React, { useState, useEffect, useMemo } from "react";
import { api } from "../../../api";
import { useQuery } from "react-query";
import { exams as examsApi } from "../../../api/exams";
import { useTranslation } from 'react-i18next';
import { ArrowRight, CalendarClock, CircleCheck, Clipboard, ClipboardList, Loader as LoaderIcon, Lock, LockOpen, Play, Search } from "lucide-react";
import Loader from "../../../components/loader/Loader";
import Pagination from "../../../components/Pagination/Pagination";
import EmptyData from "../../../components/EmptyData";
import SurveyRow, { getDaysLeft, getSurveyStatus, isSurveyLocked, ProgressBar, tint } from "../dashboard/components/SurveyRow";
import useSurveyActions from "../dashboard/components/useSurveyActions";
import LanguageSwitcher from "../../../components/LanguageSwitcher";

// Default accent when the client has no branding (matches the old orange buttons).
const DEFAULT_ACCENT = "#f97316";
// Surveys ending within this many days count as "Due soon".
const DUE_SOON_DAYS = 7;

const NextSurveyPanel = ({ survey, refetch, accent, label }) => {
  const { t } = useTranslation();
  const { takeSurvey, isLoadingQuestions, overlays } = useSurveyActions({ survey, refetch });
  const status = getSurveyStatus(survey);
  const progress = Math.round(parseFloat(survey?.progress) || 0);
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-5">
      {overlays}
      <div className="hidden sm:flex items-center pr-6 border-r border-gray-200 self-stretch">
        <ClipboardList className="w-16 h-16" strokeWidth={1.75} style={{ color: accent }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: accent }}>
          {status === "in-progress" ? t("continue-next-survey") : t("start-next-survey")}
        </p>
        <h2 className="text-xl font-bold text-gray-900 mt-1 break-words">{survey?.group_title || survey?.survey_title}</h2>
        <div className="flex items-center gap-4 mt-3">
          <ProgressBar value={progress} accent={accent} className="flex-1 max-w-[300px]" />
          <span className="text-sm text-gray-600 whitespace-nowrap">{t("percent-complete", { percent: progress })}</span>
        </div>
      </div>
      <button
        onClick={() => takeSurvey(survey)}
        disabled={isLoadingQuestions}
        style={{ backgroundColor: accent }}
        className="flex items-center justify-center gap-2 text-white font-medium py-3 px-5 rounded-md hover:opacity-90 whitespace-nowrap"
      >
        {status === "in-progress" ? t("continue-survey") : label("take-survey")}
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
};

const Stat = ({ icon, value, text, isLoading }) => (
  <div className="flex flex-col items-center justify-center text-center px-2 py-1">
    {icon}
    <p className="text-3xl font-bold text-gray-900 mt-2">{isLoading ? '-' : value}</p>
    <p className="text-sm text-gray-600 mt-1">{text}</p>
  </div>
);

const SelfReview = ({ width }) => {
  const storedBranding = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("branding"));
    } catch (error) {
      return null;
    }
  }, []);
  const appearance = storedBranding?.data?.appearance;
  const accent = appearance?.theme_colour || DEFAULT_ACCENT;

  const [showRedirect, hideRedirect] = useState(false);
  const user = JSON.parse(sessionStorage.getItem('user'))
  // Culture audit hides the sidebar (see CandidateRoutes.jsx), which is the
  // only place the client logo normally appears — show it here instead.
  const isCultureAudit = user?.sub_type === "culture";
  // Branded culture audits should only show the client's palette — swap the
  // default green "completed" styling for neutral grey/black.
  const useNeutralStatus = isCultureAudit && !!appearance?.theme_colour;
  const { t, i18n: i18nNext, } = useTranslation();
  // Culture audit participants shouldn't see "Self Evaluation" wording.
  const label = (key, options) => t(isCultureAudit ? key.replace("self-evaluations", "culture-surveys") : key, options);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(30);
  const [search, setSearch] = useState('')
  const [lockFilter, setLockFilter] = useState('all');

  const { isLoading: surveysLoading, data: surveys, refetch } = useQuery(
    ["user exams", currentPage, perPage, search],
    async () => {
      const data = await examsApi.getSurveys({
        type: null, id: user?.user?.user_id, currentPage, perPage, search, client_id: user?.client_id
      });
      return data;
    },
    {
      select: (data) => data,
      // Facilitators lock and unlock surveys at any time; pick that up
      // without a manual reload.
      refetchOnWindowFocus: true,
      refetchInterval: 30000,
    }
  );

  const { data: summaryData, isLoading: summaryLoading } = useQuery(
    ["Self survey Summary Data"],
    async () => {
      const data = await api.getDashboardSummary(user?.user?.user_id, user?.client_id);
      return data;
    },
    {
      select: (response) => response?.data,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );

  useEffect(() => {
    hideRedirect(true);
  }, []);

  // Keep the API's order (a pipeline's surveys come back in pipeline order);
  // filtering and grouping below never re-sort.
  const surveyList = surveys?.data || [];
  const openCount = surveyList.filter((survey) => !isSurveyLocked(survey)).length;
  const closedCount = surveyList.length - openCount;
  const visibleSurveys = surveyList.filter((survey) =>
    lockFilter === 'all' ? true : lockFilter === 'closed' ? isSurveyLocked(survey) : !isSurveyLocked(survey)
  );
  const groups = [
    { status: 'not-started', title: t('start-now'), icon: <Play className="w-5 h-5" fill={accent} style={{ color: accent }} /> },
    { status: 'in-progress', title: t('continue'), icon: <LoaderIcon className="w-6 h-6" style={{ color: useNeutralStatus ? accent : '#2563eb' }} /> },
    { status: 'completed', title: t('completed'), icon: <CircleCheck className="w-6 h-6 text-white" fill={useNeutralStatus ? '#111827' : '#22c55e'} /> },
  ].map((group) => ({ ...group, surveys: visibleSurveys.filter((survey) => getSurveyStatus(survey) === group.status) }))
    .filter((group) => group.surveys.length);

  const openSurveys = surveyList.filter((survey) => !isSurveyLocked(survey));
  const nextSurvey = openSurveys.find((survey) => getSurveyStatus(survey) === 'in-progress')
    || openSurveys.find((survey) => getSurveyStatus(survey) === 'not-started');
  const inProgressCount = surveyList.filter((survey) => getSurveyStatus(survey) === 'in-progress').length;
  const dueSoonCount = surveyList.filter((survey) => {
    const daysLeft = getDaysLeft(survey);
    return getSurveyStatus(survey) !== 'completed' && daysLeft !== null && daysLeft >= 0 && daysLeft <= DUE_SOON_DAYS;
  }).length;
  const typeLabel = label("survey-type-self-evaluations");

  const pills = [
    { key: 'all', text: t('all-count', { count: surveyList.length }) },
    { key: 'open', text: t('open-count', { count: openCount }), icon: LockOpen },
    { key: 'closed', text: t('closed-count', { count: closedCount }), icon: Lock },
  ];

  return (
    <React.Fragment>
      {/* Header: client logo | sub-type name, language on the right */}
      <header className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-gray-200">
        <div className="flex items-center gap-5 min-w-0">
          {isCultureAudit && appearance?.desktop_logo && (
            <>
              <img src={appearance.desktop_logo} alt="logo" className="h-10 md:h-12 object-contain" />
              <span className="w-px h-10 bg-gray-300" />
            </>
          )}
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900" style={{ margin: 0 }}>
            {label("self-evaluations")}
          </h1>
        </div>
        <LanguageSwitcher compact />
      </header>

      <div className="mt-6">
        <h2 className="text-2xl md:text-4xl font-bold text-gray-900" style={{ margin: 0 }}>{t("complete-assigned-surveys")}</h2>
        <p className="text-gray-600 text-base md:text-lg mt-2">
          {t("complete-assigned-surveys-subtitle")}
        </p>
      </div>

      <div className="mt-6 grid gap-5 grid-cols-1 lg:grid-cols-[3fr_2fr]">
        {nextSurvey ? (
          <NextSurveyPanel survey={nextSurvey} refetch={refetch} accent={accent} label={label} />
        ) : <div className="hidden lg:block" />}
        <div className="bg-white border border-gray-200 rounded-xl py-4 grid grid-cols-2 sm:grid-cols-4 gap-y-4 sm:divide-x divide-gray-200">
          <Stat isLoading={summaryLoading} value={summaryData?.self_reviews?.selfReviewAssigned || 0} text={t('assigned')}
            icon={<Clipboard className="w-7 h-7 text-gray-900" strokeWidth={2.25} />} />
          <Stat isLoading={surveysLoading} value={inProgressCount} text={t('in-progress')}
            icon={<LoaderIcon className="w-7 h-7 text-gray-900" strokeWidth={2.25} />} />
          <Stat isLoading={summaryLoading} value={summaryData?.self_reviews?.selfReviewCompleted || 0} text={t('completed')}
            icon={<CircleCheck className="w-7 h-7 text-white" fill={useNeutralStatus ? '#111827' : '#22c55e'} />} />
          <Stat isLoading={surveysLoading} value={dueSoonCount} text={t('due-soon')}
            icon={<CalendarClock className="w-7 h-7 text-gray-900" strokeWidth={2.25} />} />
        </div>
      </div>

      <section className="mt-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
          <div className="flex flex-wrap gap-3">
            {pills.map(({ key, text, icon: Icon }) => {
              const active = lockFilter === key;
              return (
                <button
                  key={key}
                  onClick={() => setLockFilter(key)}
                  aria-pressed={active}
                  style={active ? { backgroundColor: accent, borderColor: accent } : undefined}
                  className={`flex items-center gap-2 border rounded-full px-5 py-2 text-sm font-medium min-w-[96px] justify-center ${active ? 'text-white' : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'}`}
                >
                  {Icon && <Icon className="w-4 h-4" strokeWidth={2.5} />}
                  {text}
                </button>
              );
            })}
          </div>
          <label className="w-full md:w-[380px] flex items-center gap-3 bg-white border border-gray-300 rounded-md px-4 py-2.5">
            <Search className="w-5 h-5 text-gray-500 shrink-0" aria-hidden />
            <input
              type="search"
              placeholder={t('search-surveys')}
              onChange={event => setSearch(event.target.value)}
              className="w-full bg-transparent text-sm text-slate-950 focus:outline-none"
            />
          </label>
        </div>

        {
          surveysLoading ? (
            <Loader />
          ) : !groups.length ? (
            <EmptyData text={t('no-surveys-found')} />
          ) : (
            <div className="mb-12 space-y-6">
              {groups.map((group) => (
                <div key={group.status}>
                  <div className="flex items-center gap-3 mb-3">
                    {group.icon}
                    <h3 className="text-xl font-bold text-gray-900" style={{ margin: 0 }}>{group.title}</h3>
                    <span className="text-xs font-semibold text-gray-700 rounded-full px-2.5 py-1" style={{ backgroundColor: tint('#6b7280', 12) }}>
                      {group.surveys.length}
                    </span>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-200">
                    {group.surveys.map((survey) => (
                      <SurveyRow
                        key={`${survey?.user_exam_id}-${survey?.assessment?.id ?? survey?.id}`}
                        survey={survey}
                        refetch={refetch}
                        accent={accent}
                        useNeutralStatus={useNeutralStatus}
                        typeLabel={typeLabel}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        }
      </section>
      <Pagination
        data={surveys?.meta}
        perPage={perPage}
        pageNo={currentPage}
        setPageNo={setCurrentPage}
        setPerPage={setPerPage}
      />
    </React.Fragment>
  );
};

export default SelfReview;
